import React, { useState } from 'react';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import useAsync from 'react-use/esm/useAsync';

export type Environments = {
  sbx: boolean;
  dev: boolean;
  lab: boolean;
  preprod: boolean;
  prod: boolean;
};

export const EnvironmentInput = (props: {
  onChange: (value: string[]) => void;
  label?: string;
  team?: string;
  product?: string;
  clusterGroup?: string;
  namespace?: string;
  helperText?: string;
  required?: boolean;
  error?: boolean;
  value?: string[];
}) => {
  const catalogApi = useApi(catalogApiRef);
  const [available, setAvailable] = useState<string[]>([]);
  const [currentProduct, setCurrentProduct] = useState<string>();
  const [currentGroup, setCurrentGroup] = useState<string>();
  const [currentNamespace, setCurrentNamespace] = useState<string>();
  const [environments] = useState<Environments>({
    sbx: false,
    dev: false,
    lab: false,
    preprod: false,
    prod: false,
  });

  const { loading } = useAsync(async () => {
    if (props.product && props.clusterGroup && props.team && props.namespace) {
      if (
        currentProduct !== props.product ||
        currentGroup !== props.clusterGroup ||
        currentNamespace !== props.namespace
      ) {
        const entity = await catalogApi.getEntityByRef(
          `product:${props.product}`,
        );
        if (entity) {
          const availableEnvs = (entity.spec?.data as any).clusters
            ?.filter((it: any) => it.group === props.clusterGroup)
            ?.filter((it: any) => it.namespaces.includes(props.namespace))
            .map((it: any) => it.environment);
          setAvailable(availableEnvs);
          environments.sbx = availableEnvs.includes('sbx');
          environments.dev = availableEnvs.includes('dev');
          environments.lab = availableEnvs.includes('lab');
          environments.preprod = availableEnvs.includes('preprod');
          environments.prod = availableEnvs.includes('prod');
          handleChange();
        } else {
          setAvailable([]);
          environments.sbx = false;
          environments.dev = false;
          environments.lab = false;
          environments.preprod = false;
          environments.prod = false;
          handleChange();
        }
        setCurrentProduct(props.product);
        setCurrentGroup(props.clusterGroup);
        setCurrentNamespace(props.namespace);
      }
    } else {
      setAvailable([]);
      environments.sbx = false;
      environments.dev = false;
      environments.lab = false;
      environments.preprod = false;
      environments.prod = false;
      handleChange();
    }
  }, [
    props.product,
    props.clusterGroup,
    props.value,
    props.team,
    props.namespace,
  ]);

  function handleChange() {
    const envArray = Object.keys(environments).filter(
      key => (environments as any)[key],
    );
    props.onChange(envArray);
  }

  return (
    <FormControl
      required={props.required}
      error={props.error}
      component="fieldset"
    >
      <FormLabel>${props.label ?? 'Environments'}</FormLabel>
      <FormGroup row>
        <FormControlLabel
          control={
            <Checkbox
              checked={environments.sbx}
              onChange={(_, newValue) => {
                environments.sbx = newValue;
                handleChange();
              }}
              disabled={loading || !available.includes('sbx')}
            />
          }
          label="sbx"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={environments.dev}
              onChange={(_, newValue) => {
                environments.dev = newValue;
                handleChange();
              }}
              disabled={loading || !available.includes('dev')}
            />
          }
          label="dev"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={environments.lab}
              onChange={(_, newValue) => {
                environments.lab = newValue;
                handleChange();
              }}
              disabled={loading || !available.includes('lab')}
            />
          }
          label="lab"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={environments.preprod}
              onChange={(_, newValue) => {
                environments.preprod = newValue;
                handleChange();
              }}
              disabled={loading || !available.includes('preprod')}
            />
          }
          label="preprod"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={environments.prod}
              onChange={(_, newValue) => {
                environments.prod = newValue;
                handleChange();
              }}
              disabled={loading || !available.includes('prod')}
            />
          }
          label="prod"
        />
      </FormGroup>
      <FormHelperText>
        $
        {props.helperText ??
          'Please choose the environments you finally want to have.'}
      </FormHelperText>
    </FormControl>
  );
};
